import type { SelectOption } from "@ibada/ui"

/**
 * Conversion-date (accounting start date) helpers, shared by the Finance Settings setup editor and the guided
 * onboarding wizard's Step 1.
 *
 * A conversion date is a period boundary, not a specific day (the Xero convention): a church converts "as of the
 * start of a month", so the UI offers Month + Year and always means the 1st. The ledger stores it as the branch's
 * LOCAL midnight, which in a UTC+2 zone (Kigali) is the PRIOR day in UTC — so reading it back must happen in the
 * branch timezone, never by slicing the ISO string.
 */

/** The twelve months named in the app's language (not the browser's), values "1" to "12". */
export function monthOptions(locale: string): SelectOption<string>[] {
  const format = new Intl.DateTimeFormat(locale, { month: "long" })
  return Array.from({ length: 12 }, (_, index) => {
    const label = format.format(new Date(Date.UTC(2026, index, 15)))
    return { value: String(index + 1), label: label.charAt(0).toLocaleUpperCase(locale) + label.slice(1) }
  })
}

export const MONTH_OPTIONS: SelectOption<string>[] = Array.from({ length: 12 }, (_, index) => {
  const month = index + 1
  return {
    value: String(month),
    // Month names come from the browser's own calendar data rather than a translated list kept in three files.
    label: new Intl.DateTimeFormat(undefined, { month: "long" }).format(new Date(Date.UTC(2026, index, 1))),
  }
})

// The window runs from a few years back (churches often convert mid-history) to next year.
export const CURRENT_YEAR = new Date().getFullYear()
// The current calendar month (1-based), for seeding the conversion-date picker.
export const CURRENT_MONTH = new Date().getMonth() + 1
export const YEAR_OPTIONS: SelectOption<string>[] = Array.from({ length: 8 }, (_, index) => {
  const year = CURRENT_YEAR + 1 - index
  return { value: String(year), label: String(year) }
})

/**
 * The branch-local calendar day (`YYYY-MM-DD`) of a saved conversion instant, for seeding the picker.
 *
 * The instant is the branch's LOCAL midnight (June 1 in Kigali is stored as `2026-05-31T22:00Z`). A naive
 * `.slice(0, 10)` would render a June conversion date as May, so read it back in the branch's timezone.
 */
export const conversionDayInTimezone = (iso: string | null, timeZone: string): string =>
  iso
    ? new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(
        new Date(iso),
      )
    : ""

/** The 1st of `year`/`month` (1-based) as `YYYY-MM-01` — the calendar day the ledger stores. */
export const toConversionDay = (year: string, month: string): string => `${year}-${month.padStart(2, "0")}-01`

/**
 * The conversion day to OFFER when the branch has none saved yet: the 1st of the CURRENT month/year.
 * A church setting up today starts its Ibada books this month, not in some fixed January.
 */
export const defaultConversionDay = (): string => toConversionDay(String(CURRENT_YEAR), String(CURRENT_MONTH))

/**
 * The day opening balances are dated: the last day of the month BEFORE the conversion month. Convert on
 * 1 Apr 2024 and your opening balances are "as at 31 Mar 2024" — the close of the old books. Returns a localized
 * label, or null if the conversion date isn't a complete month/year yet.
 */
export const openingBalanceAsAt = (iso: string | null, locale?: string): string | null => {
  if (!iso) return null
  const [year, month] = iso.split("-").map(Number)
  if (!year || !month) return null
  const priorMonthEnd = new Date(Date.UTC(year, month - 1, 0))
  // In the app's language (not the browser's), and read in UTC: the date was built in UTC, so a viewer west of
  // Greenwich must not see the day before.
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }).format(
    priorMonthEnd,
  )
}

/** Whole calendar months from the conversion month (`YYYY-MM-DD`) to `now`'s month: the months the books cover. */
export const monthsSinceConversion = (conversionDay: string | null, now: Date = new Date()): number | null => {
  if (!conversionDay) return null
  const [year, month] = conversionDay.split("-").map(Number)
  if (!year || !month) return null
  return Math.max(0, (now.getFullYear() - year) * 12 + (now.getMonth() + 1 - month))
}

/** The city of an IANA zone ("Africa/Kinshasa" gives "Kinshasa"), for plain-words hints. */
export const timezoneCity = (zone: string | null | undefined): string | null => {
  if (!zone) return null
  const city = zone.split("/").pop()
  return city ? city.replace(/_/g, " ") : null
}
